import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ButtonBase, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ArrowRightOutlined, CaretRightFilled, CloseOutlined } from '@ant-design/icons';
import {
  DISPLAY_FONT,
  MONO_FONT,
  OVERLAY_DRAWER_PAPER_SX,
  OVERLAY_DRAWER_SX,
  RADIUS,
  REDUCED_MOTION,
  SR_ONLY_SX
} from 'components/ui/tokens';
import { focusRingSx, riseIn } from 'components/ui/styleKit';
import { analytics } from 'lib/analytics';
import { useTourMotion } from 'sections/welcome-tour/motion';
import {
  ACTION_TARGETS,
  FILTER_TRACK_ALPHA,
  LABEL_TINT_ALPHA,
  LINK_HOVER_ALPHA,
  LEVELS,
  LOAD_STATUS,
  PANEL_FILTERS,
  PANEL_MONTH_FORMAT,
  PANEL_PICTURE_HEIGHT_PX,
  PANEL_WIDTHS,
  SURFACES,
  UNREAD_BORDER_ALPHA,
  UNREAD_FILL_ALPHA
} from '../constants';
import { WHATS_NEW_COPY, postDate } from '../content';
import { useMarkSeen, useUnreadPosts, useVisiblePosts } from '../hooks/useAnnouncements';
import { postIcon, WHATS_NEW_ICON as WhatsNewIcon } from '../postIcon';
import { useAnnouncementStore } from '../store';
import { postTheme } from '../theme';
import { postSteps } from '../tour';
import type { Label, Post } from '../types';
import LabelPill from './LabelPill';
import SpotlightHero from './SpotlightHero';

const c = WHATS_NEW_COPY.panel;
const TITLE_ID = 'whats-new-panel-title';
const postTitleId = (post: Post) => `whats-new-post-${post.id}`;
type Filter = keyof typeof PANEL_FILTERS;
const ALL: Filter = 'all';
const FILTERS = Object.keys(PANEL_FILTERS) as Filter[];

const shows = (filter: Filter, post: Post): boolean => {
  const labels: readonly Label[] | null = PANEL_FILTERS[filter];
  return !labels || labels.includes(post.label);
};

// Posts grouped under their month, newest first (the posts already come newest first).
const byMonth = (posts: Post[]): [string, Post[]][] => {
  const months = new Map<string, Post[]>();
  posts.forEach((post) => {
    const month = postDate(post, PANEL_MONTH_FORMAT);
    months.set(month, [...(months.get(month) ?? []), post]);
  });
  return Array.from(months.entries());
};

/**
 * The What's new panel: every post from the last 6 months, the permanent home people come back to. Launches get a
 * picture card, everything else a compact one. Opening it shows what's unread; closing it (or Mark all as read)
 * saves those as read. A launch reopens in the popup.
 */
const WhatsNewDrawer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { reduce } = useTourMotion();
  const markSeen = useMarkSeen();
  const posts = useVisiblePosts();
  const unread = useUnreadPosts();
  const open = useAnnouncementStore((s) => s.panelOpen);
  const ready = useAnnouncementStore((s) => s.status === LOAD_STATUS.ready);
  const [filter, setFilter] = useState<Filter>(ALL);

  useEffect(() => {
    if (open) setFilter(ALL);
  }, [open]);

  // What was unread when the panel opened (or, opened early, once the state loaded) keeps its dot until it
  // closes, so people can see what's new.
  const unreadOnOpen = useRef<string[]>([]);
  const [dotted, setDotted] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!open || !ready) return;
    unreadOnOpen.current = unread.map((post) => post.id);
    setDotted(new Set(unreadOnOpen.current));
    // Only then: reading inside the panel must not move the dots.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ready]);

  const readAll = () => {
    markSeen(unreadOnOpen.current, SURFACES.panel);
    unreadOnOpen.current = [];
  };
  const close = () => {
    readAll();
    useAnnouncementStore.getState().setPanelOpen(false);
  };
  const markAllRead = () => {
    readAll();
    setDotted(new Set());
  };
  const openInPopup = (post: Post) => {
    close();
    useAnnouncementStore.getState().openPopup([post.id], SURFACES.panel);
  };
  const goTo = (post: Post, route: string) => {
    analytics.announcementClicked({ post_id: post.id, surface: SURFACES.panel, target: ACTION_TARGETS.cta });
    close();
    navigate(route);
  };

  const months = useMemo(() => byMonth((posts ?? []).filter((post) => shows(filter, post))), [posts, filter]);
  const primary = theme.palette.primary.main;
  // Teal text that stays readable on the dark panel; hover tints the button instead of recolouring the text.
  const linkColor = theme.palette.mode === 'dark' ? theme.palette.primary.lighter : primary;
  const hoverSx = { backgroundColor: alpha(primary, LINK_HOVER_ALPHA) };
  const linkSx = (color: string) => ({ px: 1, fontWeight: 600, color, '&:hover': { ...hoverSx, color } });
  const tileSx = {
    width: 40,
    height: 40,
    flex: 'none',
    display: 'grid',
    placeItems: 'center',
    borderRadius: RADIUS.control,
    fontSize: 20
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      transitionDuration={reduce ? 0 : undefined}
      sx={OVERLAY_DRAWER_SX}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': TITLE_ID,
        sx: {
          ...OVERLAY_DRAWER_PAPER_SX,
          width: PANEL_WIDTHS,
          maxWidth: '100vw',
          borderTopLeftRadius: RADIUS.panel,
          borderBottomLeftRadius: RADIUS.panel,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pl: 3, pr: 2, pt: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: 0.5 }}>
          <Typography
            id={TITLE_ID}
            component="h2"
            sx={{ m: 0, fontFamily: DISPLAY_FONT, fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}
          >
            {c.title}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13.5 }}>{c.subtitle}</Typography>
        </Box>
        <IconButton onClick={close} aria-label={c.close} sx={{ mt: -1 }}>
          <CloseOutlined aria-hidden />
        </IconButton>
      </Box>

      <Box
        role="group"
        aria-label={c.filterLabel}
        sx={{
          mx: 3,
          mt: 2.25,
          p: 0.5,
          display: 'grid',
          gridTemplateColumns: `repeat(${FILTERS.length}, minmax(0, 1fr))`,
          gap: 0.5,
          borderRadius: RADIUS.control,
          bgcolor: alpha(theme.palette.text.primary, FILTER_TRACK_ALPHA)
        }}
      >
        {FILTERS.map((key) => {
          const on = key === filter;
          return (
            <ButtonBase
              key={key}
              aria-pressed={on}
              onClick={() => setFilter(key)}
              sx={{
                ...focusRingSx(theme),
                minHeight: 36,
                borderRadius: RADIUS.tile,
                fontSize: 13,
                fontWeight: 500,
                color: on ? 'text.primary' : 'text.secondary',
                bgcolor: on ? 'background.paper' : 'transparent',
                boxShadow: on ? theme.shadows[1] : 'none',
                transition: 'background-color .2s ease, color .2s ease',
                [REDUCED_MOTION]: { transition: 'none' }
              }}
            >
              {c.filters[key]}
            </ButtonBase>
          );
        })}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', mt: 2, px: 3, pb: 2.5, borderTop: 1, borderColor: 'divider' }}>
        {posts === null && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {posts !== null && !months.length && (
          <Box sx={{ py: 7, px: 1.5, display: 'grid', justifyItems: 'center', gap: 1.5, textAlign: 'center' }}>
            <Box
              aria-hidden="true"
              sx={{
                ...tileSx,
                width: 48,
                height: 48,
                color: 'text.secondary',
                bgcolor: alpha(theme.palette.text.primary, FILTER_TRACK_ALPHA)
              }}
            >
              <WhatsNewIcon />
            </Box>
            <Typography sx={{ color: 'text.secondary', fontSize: 13.5 }}>{posts.length ? c.emptyFilter : c.empty}</Typography>
          </Box>
        )}
        {months.map(([month, items]) => (
          <Box key={month} component="section" sx={{ pt: 2.5 }}>
            <Typography
              component="h3"
              sx={{
                m: 0,
                mb: 1.25,
                fontFamily: MONO_FONT,
                fontSize: 11,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'text.secondary'
              }}
            >
              {month}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.5 }}>
              {items.map((post, i) => {
                const isUnread = dotted.has(post.id);
                const launch = post.level === LEVELS.launch;
                const steps = postSteps(post);
                const Icon = postIcon(post);
                return (
                  <Box
                    component="li"
                    key={post.id}
                    sx={{
                      ...riseIn(i),
                      overflow: 'hidden',
                      borderRadius: RADIUS.card,
                      border: 1,
                      borderColor: isUnread ? alpha(primary, UNREAD_BORDER_ALPHA) : 'divider',
                      backgroundColor: isUnread ? alpha(primary, UNREAD_FILL_ALPHA) : 'transparent'
                    }}
                  >
                    {launch && (
                      <Box
                        aria-hidden="true"
                        sx={{ position: 'relative', height: PANEL_PICTURE_HEIGHT_PX, borderBottom: 1, borderColor: 'divider' }}
                      >
                        <SpotlightHero post={post} media={steps[0].media} theme={postTheme(post, theme)} />
                        {steps.length > 1 && (
                          <Box
                            component="span"
                            sx={{
                              position: 'absolute',
                              left: 12,
                              bottom: 12,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                              px: 1.25,
                              py: 0.5,
                              borderRadius: RADIUS.pill,
                              fontSize: 11.5,
                              fontWeight: 500,
                              color: 'common.white',
                              bgcolor: alpha(theme.palette.common.black, 0.6),
                              border: `1px solid ${alpha(theme.palette.common.white, 0.16)}`
                            }}
                          >
                            <CaretRightFilled />
                            {c.tourBadge(steps.length)}
                          </Box>
                        )}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1.5, p: 2 }}>
                      {!launch && (
                        <Box aria-hidden="true" sx={{ ...tileSx, color: primary, bgcolor: alpha(primary, LABEL_TINT_ALPHA) }}>
                          <Icon />
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, fontSize: 12.5, color: 'text.secondary' }}>
                          <LabelPill label={post.label} />
                          <span>{post.area}</span>
                          <span aria-hidden="true">·</span>
                          <span>{postDate(post)}</span>
                          {isUnread && (
                            <Box
                              component="span"
                              sx={{ ml: 'auto', width: 8, height: 8, flex: 'none', borderRadius: '50%', bgcolor: 'primary.main' }}
                            >
                              <Box component="span" sx={SR_ONLY_SX}>
                                {c.unread}
                              </Box>
                            </Box>
                          )}
                        </Box>
                        <Typography id={postTitleId(post)} component="h4" sx={{ m: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.35 }}>
                          {post.title}
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: 13.5, color: 'text.secondary', lineHeight: 1.55 }}>{post.summary}</Typography>
                        {(launch || post.cta) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1, mx: -1 }}>
                            {launch && (
                              <Button
                                size="small"
                                onClick={() => openInPopup(post)}
                                aria-describedby={postTitleId(post)}
                                sx={{
                                  ...linkSx(theme.palette.text.secondary),
                                  fontWeight: 500,
                                  '&:hover': { ...hoverSx, color: 'text.primary' }
                                }}
                              >
                                {c.open}
                              </Button>
                            )}
                            {post.cta && (
                              <Button
                                size="small"
                                onClick={() => goTo(post, post.cta!.route)}
                                endIcon={<ArrowRightOutlined aria-hidden />}
                                sx={linkSx(linkColor)}
                              >
                                {post.cta.label}
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        <Button size="small" onClick={markAllRead} disabled={!dotted.size} sx={linkSx(linkColor)}>
          {c.markAllRead}
        </Button>
        <Typography sx={{ px: 1, fontSize: 12.5, color: 'text.secondary' }}>{c.keptFor}</Typography>
      </Box>
    </Drawer>
  );
};

export default WhatsNewDrawer;
